import { call, take, takeLatest, takeEvery, put, fork, select } from 'redux-saga/effects'

import { indicateProgress } from 'api'
import { fetchLocationByGrsId, fetchLocationById } from 'sfproxy/api'
import { redeemPromoCode } from 'basket/api'
import * as grs from 'grs'
import getRandomness from 'shared/random-generator'
import featureToggles from 'shared/featureToggles'
import { REMOVE_VEHICLE } from 'dashboard-vehicles/constants'
import { SHOW_MONTHLY_FLOW, SHOW_PREPAID_FLOW } from 'booking/constants'
import { SET_EVENT } from 'search/constants'
import { addEventDetails } from 'search/actions'
import { searchDateTimeSelector } from 'search-date-time'
import { languageSelector } from 'i18n/selectors'

import * as constants from './constants'
import * as actions from './actions'
import {
    rateSelector,
    selectedRateIdSelector,
    selectedVehicleSelector,
    rawPromoCodeSelector,
    currentCustomerFlowSelector,
} from './selectors'
import { RatesError } from './errors'
import { createDenormalizedSelector } from 'entities/selectors'
import { rateSchema } from 'grs/schemas'
import { apiLanguageCodeSelector } from 'i18n/selectors'
import { userSelector } from 'auth/selectors'
import { customerFlowType } from 'grs/helpers'
import { searchModeSelector } from 'map/selectors'

const locationRatesPredicate = (requestId, action) =>
    [grs.constants.RECEIVED_LOCATION_RATES, grs.constants.FAILED_TO_FETCH_RATES].indexOf(action.type) !== -1 &&
    action.requestId === requestId

function* getRates({ grsId, preselectedRateId, extendingStart, extendingEnd, isExtending }) {
    const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))
    const currentCustomerFlow = yield select(currentCustomerFlowSelector)

    yield put(actions.setRates(null))
    const requestId = getRandomness()
    const { start: formStart, end: formEnd } = yield select(searchDateTimeSelector)
    const beginTime = extendingStart || formStart
    const endTime = extendingEnd || formEnd
    yield put(
        grs.actions.fetchRates(
            [{ grsId, beginTime, endTime, preselectedRateId, isExtending }],
            requestId,
            currentCustomerFlow || currentCustomerFlowType
        )
    )
    const { result, type, error } = yield take(locationRatesPredicate.bind(null, requestId))
    if (type === grs.constants.FAILED_TO_FETCH_RATES) throw new RatesError(error.message)

    const $rateId = yield select(selectedRateIdSelector)

    if (preselectedRateId || $rateId) {
        let rateId = preselectedRateId

        if (!rateId) {
            const selectedRate = yield select(createDenormalizedSelector($rateId, rateSchema))
            rateId = selectedRate.rateId
        }
        const receivedRates = yield select(createDenormalizedSelector(result, [rateSchema]))

        const matchingRate = receivedRates.find(rate => rate.rateId === rateId)

        if (matchingRate) yield put(actions.selectRate(matchingRate.$rateId))
    }

    yield put(actions.setRates(result))
}

export function* watchGetRates() {
    yield takeLatest(constants.GET_RATES, function* (action) {
        try {
            yield call(indicateProgress, 'bookingRates', getRates, action)
        } catch (error) {}
    })
}

export function* watchSelectRate() {
    yield takeEvery(constants.SELECT_RATE, function* () {
        const { numberOfVehicles } = yield select(rateSelector)
        const selectedVehicles = yield select(selectedVehicleSelector)

        if (numberOfVehicles && numberOfVehicles < selectedVehicles.length)
            yield put(actions.selectVehicle(selectedVehicles.slice(0, numberOfVehicles)))
    })
}

export function* watchSetEvent() {
    const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))

    yield takeEvery(SET_EVENT, function* ({ event }) {
        const { fromDate, toDate, locationId, rateId } = event
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)

            const { rateTable } = yield call(grs.api.getRateDetails, {
                locationId,
                rateId,
                fromDate,
                toDate,
                waitingListKey: null,
                apiLanguageCode,
                customerFlowType: currentCustomerFlowType,
            })
            yield put(addEventDetails({ rateTable }))
        } catch (error) {}
    })
}

export function* watchRemoveVehicle() {
    yield takeEvery(REMOVE_VEHICLE, function* ({ vehicle }) {
        yield put(actions.removeFromSelectedVehicles(vehicle))
    })
}

export function* getBookingLocationDetails({ grsId }) {
    const language = yield select(languageSelector)

    try {
        const locationDetails = yield call(fetchLocationByGrsId, grsId, language)
        const defaultCustomerFlow = yield call(grs.api.getDefaultCustomerFlow, [grsId], language)
        if (locationDetails) {
            const locationId = locationDetails.result
            if (featureToggles.caPurchaseFlow) {
                const customerFlow = defaultCustomerFlow
                    .split('|')
                    .filter(flow => flow !== SHOW_PREPAID_FLOW[0] && flow !== SHOW_MONTHLY_FLOW[0])
                    .join('|')

                yield put(
                    actions.setBookingLocationDetails({
                        ...locationDetails.entities.locations[locationId],
                        defaultCustomerFlow: customerFlow,
                    })
                )
            } else
                yield put(
                    actions.setBookingLocationDetails({
                        ...locationDetails.entities.locations[locationId],
                        defaultCustomerFlow,
                    })
                )
        }
    } catch (e) {
        // Swallow error to prevent blocking UI
    }
}

export function* getBookingLocationDetailsMonthlyParking() {
    yield takeEvery(constants.GET_LOCATION_DATA_BY_ID, function* ({ grsId }) {
        const language = yield select(languageSelector)

        try {
            const locationDetails = yield call(fetchLocationById, grsId, language)
            if (locationDetails) {
                const locationId = locationDetails.result
                yield put(
                    actions.setBookingLocationDetails({
                        ...locationDetails.entities.locations[locationId],
                    })
                )
            }
        } catch (e) {
            // Swallow error to prevent blocking UI
        }
    })
}

export function* watchGetBookingLocationDetails() {
    yield takeEvery(constants.GET_BOOKING_LOCATION_DETAILS, function* (action) {
        try {
            yield call(indicateProgress, 'locationDetails', getBookingLocationDetails, action)
        } catch (error) {}
    })
}

function* lastBookingPromoCodeSaga() {
    yield takeEvery(constants.LAST_BOOKING_REDEEM_PROMO_CODE, function* ({ item }) {
        try {
            const rawPromoCode = yield select(rawPromoCodeSelector)
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const user = yield select(userSelector)

            const result = yield call(
                indicateProgress,
                `redeemPromoCode${item.id}`,
                redeemPromoCode,
                item,
                rawPromoCode,
                apiLanguageCode,
                user?.token
            )
            yield put(actions.lastBookingRedeemedPromoCode(result))
        } catch (exception) {}
    })
}

export function* watchGetSeasonTicketsSaga() {
    yield takeEvery(grs.constants.GET_SEASON_TICKETS, function* ({ locationId, startDate }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)

            yield call(indicateProgress, 'getSeasonTickets', function* () {
                const tickets = yield call(grs.api.fetchSeasonTickets, locationId, apiLanguageCode, startDate)
                yield put(grs.actions.receivedSeasonTickets(tickets))
            })
        } catch (error) {}
    })
}

export function* watchSelectSeasonTicketSaga() {
    yield takeEvery(constants.SELECT_SEASON_TICKET, function* ({ ticket, grsId, startDate, waitingListKey }) {
        if (ticket.isOnWaitingList && !waitingListKey) return

        try {
            const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))
            const apiLanguageCode = yield select(apiLanguageCodeSelector)

            const details = yield call(indicateProgress, 'getSeasonTicketRate', grs.api.getRateDetails, {
                locationId: grsId,
                rateId: ticket.id || ticket.rateId,
                beginTime: startDate || ticket.nextAvailableDateISO || new Date(),
                endTime: null,
                waitingListKey,
                apiLanguageCode,
                customerFlowType: currentCustomerFlowType,
            })

            yield put(actions.receivedSelectedSeasonTicketRate(details))
        } catch (error) {}
    })
}

function* watchWaitingListSaga() {
    const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))

    yield takeEvery(grs.constants.VALIDATED_WAITING_LIST_KEY, function* ({ waitingListKey, response }) {
        const { rateId, locationId, fromDate } = response
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        const rate = yield call(indicateProgress, 'getWaitingListRate', grs.api.getRateDetails, {
            locationId,
            rateId,
            fromDate,
            endTime: null,
            waitingListKey,
            apiLanguageCode,
            customerFlowType: currentCustomerFlowType,
        })

        yield put(actions.receivedWaitingListRate(rate))
    })
}

export function* getUpsellOptionsSaga() {
    yield takeEvery(constants.GET_UPSELL_OPTIONS, function* ({ rateId, lotId, beginTime, endTime }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const response = yield call(
                indicateProgress,
                'getUpSellOptions',
                grs.api.getUpSellOptions,
                rateId,
                lotId,
                beginTime,
                endTime,
                apiLanguageCode
            )
            yield put(actions.setUpSellOptions(response))
        } catch (exception) {
            yield put(actions.failedToFetchUpSellOptions(exception))
        }
    })
}

export function* getAdditionParkingItemsSaga() {
    yield takeEvery(constants.GET_ADDITIONAL_PARKING_ITEMS, function* ({ rateId, lotId }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const response = yield call(
                indicateProgress,
                constants.GET_ADDITIONAL_PARKING_ITEMS,
                grs.api.getAdditionalParkingItems,
                rateId,
                lotId,
                apiLanguageCode
            )
            yield put(actions.setAdditionalParkingItems(response))
        } catch (exception) {
            yield put(actions.getAdditionalParkingItemsError(exception))
        }
    })
}

export function* upgradeUpSellRateSaga() {
    yield takeEvery(
        constants.UPGRAGE_UPSELL_RATE,
        function* ({ locationId, rateId, beginTime, endTime, startedWithProductId }) {
            try {
                const apiLanguageCode = yield select(apiLanguageCodeSelector)
                const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))

                const details = yield call(indicateProgress, 'upgradeUpSellOption', grs.api.getRateDetails, {
                    locationId,
                    rateId,
                    beginTime,
                    endTime,
                    apiLanguageCode,
                    customerFlowType: currentCustomerFlowType,
                    startedWithProductId,
                })
                yield put(
                    actions.setLastBookingDetails({ grsId: details.locationId, rateId: details.rateId, rate: details })
                )
                yield put(actions.selectRate(details))
            } catch (exception) {}
        }
    )
}

export default function* rootSaga() {
    yield fork(watchGetRates)
    yield fork(watchSelectRate)
    yield fork(watchRemoveVehicle)
    yield fork(watchGetBookingLocationDetails)
    yield fork(lastBookingPromoCodeSaga)
    yield fork(getUpsellOptionsSaga)
    yield fork(upgradeUpSellRateSaga)
    yield fork(getBookingLocationDetailsMonthlyParking)
    yield fork(watchGetSeasonTicketsSaga)
    yield fork(watchSelectSeasonTicketSaga)
    yield fork(watchWaitingListSaga)
    yield fork(watchSetEvent)
    yield fork(getAdditionParkingItemsSaga)
}
